import React, { useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import Bulletin from "components/Bulletin";
import Loading from "components/Loading";
import TopicList from "components/Topic/List";
import AdminHeader from "components/AdminHeader";
import TopicListController from "components/Topic/ListController";
import TopicToolBar from "components/Topic/TopicToolBar";
import styles from "./index.module.css";
import {
    UPDATE_TOPICS_START,
    UPDATE_TOPICS_SUCCESS,
    UPDATE_TOPICS_FAILURE,
    TOGGLE_TOPICS_SORT_METHOD,
    TOPICS_SORT_METHODS,
} from "./constants";
import { UPDATE_APP_ADDRESS } from "views/App/constants";
import { CATEGORY_TYPES } from "../../constants";
import { useTranslation } from "react-i18next";
import { devOnlyLog } from "../../utility/utility";

const Topics = () => {
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const dispatch = useDispatch();

    const forumName = useSelector((state) => state.app.forumName);
    const forumId = useSelector((state) => state.app.forumId);
    let isAdmin = useSelector((state) => state.app.isAdmin);
    const isSuperUser = useSelector((state) => state.app.isSuperUser);
    const accessToken = useSelector((state) => state.app.accessToken);
    const sortMethod = useSelector((state) => state.topics.sortMethod);
    const topicList = useSelector((state) => state.topics.topicList);
    const isLoading = useSelector((state) => state.topics.isLoading);
    const [justThisForumData, setJustThisForumData] = useState(null);

    // Check if we're in development mode
    const isDevelopment =
        (process.env.REACT_APP_ENV || "").toLowerCase() !== "production";

    // Cache expiration time (5 minutes in milliseconds)
    const CACHE_EXPIRATION = 5 * 60 * 1000;

    useEffect(() => {
        initTopics();
    }, []);

    const initTopics = () => {
        const ID_LANG_REGEX = /-(\w+)-(\w+)$/;
        let forumNameFromUrl = "";
        let forumId = 0;

        const pathnameParts = location.pathname.split("/");
        const forumNameParam = pathnameParts[2];

        if (
            location.state !== undefined &&
            location.state.forumId !== undefined
        ) {
            forumNameFromUrl = forumNameParam.replace(ID_LANG_REGEX, "");
            forumId = location.state.forumId;
        } else {
            const matches = forumNameParam.match(ID_LANG_REGEX);
            if (matches && matches.length > 1) {
                forumId = matches[1];
            }
            forumNameFromUrl = forumNameParam.replace(ID_LANG_REGEX, "");
        }

        updateRedux(forumNameFromUrl, forumId, isAdmin);
        getTopicsAndBulletin(forumId);
    };

    const updateRedux = (useThisForumName, useThisForumId, useIsAdmin) => {
        dispatch({
            type: UPDATE_APP_ADDRESS,
            forumName: useThisForumName,
            forumId: useThisForumId,
            isAdmin: useIsAdmin,
        });
    };

    const getTopicsAndBulletin = async (thisForumId) => {
        try {
            thisForumId = thisForumId || forumId;

            if (!thisForumId) {
                console.error("Forum ID not available");
                return;
            }

            dispatch({ type: UPDATE_TOPICS_START });

            // Always get fresh topic data
            const resTopic = await axios.get(
                `/api/forum/${thisForumId}/topic?sort_method=${sortMethod}`
            );
            dispatch({
                type: UPDATE_TOPICS_SUCCESS,
                topicList: resTopic.data,
            });

            // Get forum data based on environment
            await fetchForumData(thisForumId);
        } catch (error) {
            console.error("Error fetching data:", error);
            dispatch({ type: UPDATE_TOPICS_FAILURE, error: error.message });
        }
    };

    const fetchForumData = async (forumId) => {
        const cacheKey = `forumData-${forumId}`;
        const cacheTimeKey = `forumDataTime-${forumId}`;
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTime = localStorage.getItem(cacheTimeKey);
        const currentTime = Date.now();

        // "new"가 URL 끝에 있으면 강제 fresh fetch
        const forceFetch = location.pathname
            .trim()
            .toLowerCase()
            .endsWith("new");

        if (
            isDevelopment ||
            forceFetch ||
            !cachedData ||
            !cachedTime ||
            currentTime - parseInt(cachedTime) >= CACHE_EXPIRATION
        ) {
            try {
                const { data } = await axios.get(`/api/forum/byId/${forumId}`);
                setJustThisForumData(data);

                if (!isDevelopment && !forceFetch) {
                    // "new"면 캐시는 건너뛰고 저장하지 않음
                    localStorage.setItem(cacheKey, JSON.stringify(data));
                    localStorage.setItem(cacheTimeKey, currentTime.toString());
                }

                devOnlyLog(
                    isDevelopment
                        ? "Dev 모드: Fetched fresh 여행 data"
                        : forceFetch
                        ? "강제 new fetch: 캐시 무시"
                        : "Prod 모드: 여행 data fetched from DB"
                );
                return;
            } catch (error) {
                console.error(`Error fetching forum data:`, error);
            }
        }

        // Use cached data if API fails or cache is valid
        if (cachedData) {
            setJustThisForumData(JSON.parse(cachedData));
            devOnlyLog(
                isDevelopment
                    ? "Dev 모드: 캐쉬된 여행데이터 사용"
                    : "Prod 모드: 캐쉬된 여행데이터 사용"
            );
        }
    };

    const toggleSortMethod = (newMethod) => {
        dispatch({
            type: TOGGLE_TOPICS_SORT_METHOD,
            sortMethod: newMethod,
        });
    };

    const topicToolBarRef = useRef();
    const handleTopicToolBarAction = () => {
        if (topicToolBarRef.current) {
            topicToolBarRef.current.handleOpen(CATEGORY_TYPES.TourQuestion);
        }
    };

    return (
        <div className={styles.topicContainer}>
            {isSuperUser ? <AdminHeader forumId={forumId} /> : ""}
            {(isLoading || !justThisForumData) && <Loading />}
            {justThisForumData && !isLoading && (
                <div>
                    <Bulletin
                        value={justThisForumData}
                        forumId={forumId}
                        onAction={handleTopicToolBarAction}
                    />
                    <div>
                        <div className={styles.boardMeTooTopBox}>
                            {t("TripBoardHeader")}
                        </div>
                        <div className={styles.listContainer}>
                            <TopicListController
                                topicSortMethods={TOPICS_SORT_METHODS}
                                toggleSortMethod={toggleSortMethod}
                            />
                            {isLoading && <Loading />}
                            <TopicList
                                isLoading={isLoading}
                                forumName={forumName}
                                forumId={forumId}
                                items={topicList}
                            />
                            <TopicToolBar
                                ref={topicToolBarRef}
                                url={`/api/forum/${forumId}/topic`}
                                accessToken={accessToken}
                                onSubmit={getTopicsAndBulletin}
                                tourData={justThisForumData}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Topics;
